import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { outputToObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslationService } from '@df/ui/translation/translation.service';
import { merge } from 'rxjs/internal/observable/merge';
import { Subscription } from 'rxjs/internal/Subscription';
import { CarouselComponent } from '../carousel.component';

/**
 * @example
 * <carousel #carouselRef></carousel>
 *
 * <carousel-dots [carouselRef]="carouselRef"></carousel-dots>
 */
@Component({
    selector: 'carousel-dots',
    templateUrl: './carousel-dots.component.html',
    styleUrl: './carousel-dots.component.scss',
    standalone: true,
    imports: [NgClass],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CarouselDotsComponent implements OnInit, OnDestroy {
    carouselRef = input.required<CarouselComponent>();
    paginationClass = input<string>();

    index = signal<number>(0);
    bullets = signal<HTMLElement[]>([]);

    protected changeDetectorRef = inject(ChangeDetectorRef);
    protected translationService = inject(TranslationService);
    protected destroyRef = inject(DestroyRef);

    private subscription?: Subscription;

    ngOnInit(): void {
        this.carouselRef()
            .swiperRef$.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.update());
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    slideTo(index = 0) {
        const swiper = this.carouselRef().swiperRef();
        const slidesPerGroup = swiper?.params?.slidesPerGroup || 1;
        const slideTo = index * slidesPerGroup;

        if (swiper?.params.loop) {
            swiper.slideToLoop(slideTo);
        } else {
            swiper?.slideTo(slideTo);
        }
    }

    protected update() {
        this.subscription?.unsubscribe();

        this.subscription = merge(
            outputToObservable(this.carouselRef().s_afterInit),
            outputToObservable(this.carouselRef().s_slideChange)
        ).subscribe(() => this.onUpdate());

        this.onUpdate();
    }

    protected onUpdate() {
        const swiper = this.carouselRef().swiperRef();
        const slidesPerGroup = this.carouselRef().slidesPerGroup() || 1;

        if (swiper) {
            this.bullets.set(swiper.pagination?.bullets || []);
            const index = slidesPerGroup > 1 ? swiper.snapIndex : swiper.realIndex;

            this.index.set(index);

            this.changeDetectorRef.detectChanges();
        }
    }
}
